import { Dialog, DialogContent, DialogHeader } from "@components/common/draggable-dialog"
import { useModal } from "@stores/modal-store"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import { PROJECT_BY_ID_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { useQuery } from "@tanstack/react-query"
import PmsLoader from "@components/common/loader"

import { useAuth } from "@stores/auth-store"
import { useEffect, useState } from "react"
import { Copy, Maximize } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { cn } from "@pma/utils/helpers"
import useTranslator from "@pma/hooks/use-translator"
import type { DraggableEvent, DraggableData } from "react-draggable"
import { FilesView } from "./FileView"

interface Props {
  projectId: number
  refetchAllFolder: () => void
}
const PreviewFolder = ({ projectId, refetchAllFolder }: Props) => {
  const modal = useModal()
  const { setActiveFolder } = useAuth()

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [isMaximized, setIsMaximized] = useState(true)
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })
  const [grabbed, setGrabbed] = useState(false)
  const { locale, translate } = useTranslator()
  const isHebrew = locale === "he"
  const getProject = async () => {
    const response = await axios.get(SERVER_URL + PROJECT_BY_ID_ENDPOINT + `/${projectId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }

  const { data: project, isLoading } = useQuery({
    queryKey: ["get-project", projectId],
    queryFn: getProject,
    enabled: projectId == 0 ? false : true,
  })
  const closeModal = () => {
    modal.onClose()
    modal.setContext(undefined)
    setActiveFolder({})
    return
  }

  useEffect(() => {
    return () => closeModal()
  }, [])

  if (!projectId) {
    return <></>
  }

  const handleMaximized = () => {
    setIsMaximized(!isMaximized)
    setPosition({ x: 0, y: 0 })
  }

  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    if (isMaximized) return
    setPosition({ x: data.x, y: data.y })
  }

  const folderContent = (
    <>
      <DialogHeader
        className={`relative text-lg font-semibold ${isHebrew ? "rtl sm:text-right" : "ltr"}`}
      >
        <div
          className={cn(
            isHebrew ? "pl-14" : "pr-14",
            !isMaximized ? `${grabbed ? "cursor-grabbing" : "cursor-grab"} dialog-drag-handle` : "",
          )}
        >
          {project?.title || "---"}
        </div>
        <div className='flex justify-end'>
          {isMaximized ? (
            <Tooltip>
              <TooltipTrigger
                onFocusCapture={(e) => {
                  // To Stop tooltip open by default
                  e.preventDefault()
                  e.stopPropagation()
                }}
                className={cn("absolute bottom-5", isHebrew ? "left-4" : "right-4")}
              >
                <Copy
                  className={`absolute top-[-20px] cursor-pointer ${isHebrew ? "left-[5px]" : "right-[5px]"}`}
                  size={14}
                  onClick={handleMaximized}
                />
              </TooltipTrigger>
              <TooltipContent side='bottom'>
                <h4>{translate("Minimize")}</h4>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger
                onFocusCapture={(e) => {
                  // To Stop tooltip open by default
                  e.preventDefault()
                  e.stopPropagation()
                }}
                className={cn("absolute bottom-5", isHebrew ? "left-4" : "right-4")}
              >
                <Maximize
                  className={`absolute top-[-20px] cursor-pointer ${isHebrew ? "left-[5px]" : "right-[5px]"}`}
                  size={14}
                  onClick={handleMaximized}
                />
              </TooltipTrigger>
              <TooltipContent side='bottom'>
                <h4>{translate("Maximize")}</h4>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </DialogHeader>

      {isLoading ? (
        <PmsLoader />
      ) : (
        <FilesView
          project={project}
          closeModal={closeModal}
          showAsModal={true}
          refetchAllFolder={refetchAllFolder}
        />
      )}
    </>
  )
  return (
    <Dialog open={modal.isOpen && modal.context === "folder"} onOpenChange={closeModal}>
      {!isMaximized ? (
        <DialogContent
          key={isMaximized ? "max" : "min"}
          dragHandleClass='.dialog-drag-handle'
          className={`flex flex-col gap-2 ${isMaximized ? "h-full max-w-full" : "h-[90vh] w-full md:max-w-[90vw] lg:max-w-[80vw]"}`}
          position={isMaximized ? undefined : position}
          onStart={() => setGrabbed(true)}
          onStop={() => setGrabbed(false)}
          onDragHandler={handleDrag}
        >
          {folderContent}
        </DialogContent>
      ) : (
        <DialogContent className='flex h-full max-w-full flex-col gap-2'>
          {folderContent}
        </DialogContent>
      )}
    </Dialog>
  )
}

export default PreviewFolder
